import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Calculator, Heart, Brain, Ribbon, Droplet, ChevronRight, Loader2, Sparkles,
} from "lucide-react";
import NephrologyCalculators from "@/components/calculators/NephrologyCalculators";
import { useTelemetry } from "@/lib/telemetry";

const SPECIALTY_META = {
  cardiology: { icon: Heart,   accent: "#F87171", label: "Cardiology" },
  neurology:  { icon: Brain,   accent: "#A78BFA", label: "Neurology" },
  oncology:   { icon: Ribbon,  accent: "#FBBF24", label: "Oncology" },
  nephrology: { icon: Droplet, accent: "#4ADE80", label: "Nephrology" },
  endocrinology: { icon: Droplet, accent: "#8B5CF6", label: "Endocrinology" },
};

/**
 * Calculators Hub — Phase 2.
 *
 * Cross-specialty index of every scoring tool the platform ships.
 * Nephrology calculators run inline (eGFR/KFRE/ACR — legacy). Cardio,
 * Neuro, Onco calculators deep-link to their specialty console at the
 * exact tab that owns the score.
 */
export default function Calculators() {
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = searchParams.get("tab") || "nephrology";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/platform/calculators");
        setCatalog(data.by_specialty || {});
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const setTab = (t) => setSearchParams({ tab: t });
  useTelemetry("calc-hub", "tab_switch", activeTab);

  const TAB_ORDER = ["nephrology", "cardiology", "neurology", "oncology", "endocrinology"];

  return (
    <div className="space-y-6" data-testid="calculators-hub">
      <div className="ros-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: "rgba(96,165,250,0.14)", border: "1px solid rgba(96,165,250,0.4)" }}>
            <Calculator size={22} style={{ color: "#60A5FA" }} />
          </div>
          <div>
            <h2 className="font-heading text-2xl">Calculators hub</h2>
            <p className="text-sm text-[#A3ACA7] mt-0.5">
              Every validated scoring tool across Nephrology, Cardiology, Neurology and Oncology — one hub.
            </p>
          </div>
        </div>
      </div>

      {/* Department tabs */}
      <div className="flex flex-wrap gap-2" data-testid="calc-tabs">
        {TAB_ORDER.map((s) => {
          const meta = SPECIALTY_META[s];
          const Icon = meta.icon;
          const active = activeTab === s;
          const count = (catalog[s] || []).length;
          return (
            <button key={s} onClick={() => setTab(s)}
                    data-testid={`calc-tab-${s}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                      active ? "text-black border-transparent"
                             : "bg-[#0F1312] text-[#A3ACA7] border-[#272E2B] hover:text-white"
                    }`}
                    style={active ? { background: meta.accent } : {}}>
              <Icon size={14} /> {meta.label}
              <span className="text-[10px] opacity-70">· {count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center text-[#A3ACA7] py-16 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Loading catalog…
        </div>
      ) : activeTab === "nephrology" ? (
        <NephrologyCalculators />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`calc-cards-${activeTab}`}>
          {(catalog[activeTab] || []).map((c) => {
            const meta = SPECIALTY_META[activeTab];
            const Icon = meta.icon;
            return (
              <button key={c.id} onClick={() => navigate(c.route)}
                      data-testid={`calc-card-${c.id}`}
                      className="text-left ros-card hover:border-[#60A5FA]/40 transition-colors group">
                <div className="flex items-start justify-between gap-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                       style={{ background: `${meta.accent}22`, border: `1px solid ${meta.accent}55` }}>
                    <Icon size={18} style={{ color: meta.accent }} />
                  </div>
                  <ChevronRight size={16} className="text-[#7A8480] group-hover:text-white transition-colors" />
                </div>
                <div className="mt-3 font-heading text-lg text-white">{c.title}</div>
                <div className="text-xs text-[#A3ACA7] mt-1 leading-relaxed">{c.description}</div>
                <div className="mt-3 flex items-center gap-2">
                  <Sparkles size={10} className="text-[#7A8480]" />
                  <span className="text-[10px] uppercase tracking-wider text-[#7A8480]">{c.guideline}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
